import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@/lib/api/client';
import { fetchNotifications, markNotificationsSeen } from '@/services/notificationService';
import type { NotificationPage } from '@/types/api';

/**
 * One query serves both the badge and the panel.
 *
 * The count comes back with the first page, so the bell knows its number without a
 * second endpoint, and opening the panel costs nothing — the rows are already here.
 */
export function useNotifications() {
  return useQuery<NotificationPage, ApiError>({
    queryKey: ['notifications'],
    queryFn: () => fetchNotifications(30),
  });
}

export function useMarkNotificationsSeen() {
  const queryClient = useQueryClient();

  return useMutation<{ seenAt: string }, ApiError, void>({
    mutationFn: markNotificationsSeen,
    /*
      Patched in place rather than invalidated. We already know the outcome — the badge
      is zero — and refetching thirty rows to learn that is a request for nothing.

      The per-row `unseen` flag is left alone deliberately: the highlight should survive
      the panel being open, so you can still see what was new while you read it.
    */
    onSuccess: ({ seenAt }) => {
      queryClient.setQueryData<NotificationPage>(['notifications'], (page) =>
        page ? { ...page, unseenCount: 0, seenAt } : page,
      );
    },
  });
}
